import wootConstants from 'dashboard/constants/globals';
import {
  hasSlaData,
  SLA_WARNING_WINDOW_MS,
} from 'dashboard/components/widgets/conversation/helpers/cardDurations';

const STATUS = wootConstants.STATUS_TYPE;

export const SLA_STATE = { OK: 'ok', WARNING: 'warning', MISSED: 'missed' };

// Same rule as the list's SLA chip (SlaTime): the nearer of the first/next
// response deadlines plus the resolution deadline; missed once any has passed,
// warning inside SLA_WARNING_WINDOW_MS. Only a running clock has a state: a
// paused, finished (resolved conversation) or inactive SLA keeps its old
// deadlines, which would read as missed — so, like no applied SLA, null.
export function getSlaState(sla, now = Date.now()) {
  if (!hasSlaData(sla)) return null;
  if (sla.status && sla.status !== 'active') return null;
  const responses = [sla.first_response_time, sla.next_response_time].filter(
    Boolean
  );
  const deadlines = [
    responses.length ? Math.min(...responses) : null,
    sla.time_to_resolve,
  ].filter(Boolean);
  if (deadlines.some(deadline => now > deadline * 1000)) {
    return SLA_STATE.MISSED;
  }
  if (
    deadlines.some(deadline => deadline * 1000 - now < SLA_WARNING_WINDOW_MS)
  ) {
    return SLA_STATE.WARNING;
  }
  return SLA_STATE.OK;
}

// Sentinel key for conversations that have no value for the grouping field.
export const NO_VALUE_KEY = '__no_value__';

// Grouping modes the board understands.
export const GROUP_BY_TYPE = {
  STATUS: 'status',
  CUSTOM_ATTRIBUTE: 'custom_attribute',
};

// Default column definitions when grouping by the native conversation status.
// Colors come from the Kanban prototype palette; titles are resolved by the
// caller (i18n) so this helper stays presentation-agnostic.
export const DEFAULT_STATUS_COLUMNS = [
  { id: STATUS.OPEN, color: '#378ADD' },
  { id: STATUS.PENDING, color: '#EF9F27' },
  { id: STATUS.SNOOZED, color: '#888780', acceptsDrop: false },
  { id: STATUS.RESOLVED, color: '#1D9E75' },
];

// Resolve which column a conversation belongs to, for a given grouping config.
// `groupBy` shape: { type: GROUP_BY_TYPE, key: String }.
export function getGroupValue(chat, groupBy) {
  if (!chat || !groupBy) return NO_VALUE_KEY;

  if (groupBy.type === GROUP_BY_TYPE.CUSTOM_ATTRIBUTE) {
    const value = chat.custom_attributes?.[groupBy.key];
    return value === undefined || value === null || value === ''
      ? NO_VALUE_KEY
      : String(value);
  }

  // Native status grouping (default).
  return chat.status || NO_VALUE_KEY;
}

// Hard cap on how many columns the board will fetch in one pass. Status mode
// has 4 columns; custom-attribute mode can explode into one request per value,
// so we bound the fan-out to protect the API. Callers must surface (log) when
// they truncate beyond this so coverage gaps are never silent.
export const MAX_KANBAN_COLUMNS = 20;

// Build the `/conversations/filter` payload that backfills a single column.
// Each column issues its own request: the column predicate (status value or
// custom-attribute value) plus the board's active global filters, all ANDed.
// Returns `{ queryData: { payload }, sortBy }` ready for `ConversationApi.filter`.
//
// `baseFilters` mirrors ChatList's `conversationFilters`
// ({ inboxId, assigneeType, teamId, labels, sortBy }). `currentUserId` is only
// needed to resolve the "me" assignee filter into an explicit assignee_id.
export function buildColumnQuery({
  columnId,
  groupBy,
  baseFilters = {},
  userFilterPayload = [],
  currentUserId,
}) {
  const predicates = [];

  // 1. The column predicate — what makes this column distinct.
  if (groupBy?.type === GROUP_BY_TYPE.CUSTOM_ATTRIBUTE) {
    predicates.push(
      columnId === NO_VALUE_KEY
        ? {
            attribute_key: groupBy.key,
            custom_attribute_type: 'conversation_attribute',
            filter_operator: 'is_not_present',
            values: [],
          }
        : {
            attribute_key: groupBy.key,
            custom_attribute_type: 'conversation_attribute',
            filter_operator: 'equal_to',
            values: [columnId],
          }
    );
  } else {
    // Status grouping: the global status filter is intentionally ignored here —
    // each column already pins its own status, so honoring it would blank out
    // every other column.
    predicates.push({
      attribute_key: 'status',
      filter_operator: 'equal_to',
      values: [columnId],
    });
  }

  // 2. The board's active global filters, applied to every column alike.
  if (baseFilters.inboxId) {
    predicates.push({
      attribute_key: 'inbox_id',
      filter_operator: 'equal_to',
      values: [baseFilters.inboxId],
    });
  }
  if (baseFilters.teamId) {
    predicates.push({
      attribute_key: 'team_id',
      filter_operator: 'equal_to',
      values: [baseFilters.teamId],
    });
  }
  // The assignee tab is chrome the LIST drops as soon as a filter is applied
  // (the tabs are hidden and pagination switches to the `appliedFilters` key),
  // but `activeAssigneeTab` keeps its default value of `me`. Honoring it here
  // would silently narrow the board to the current user's conversations while
  // the list shows everyone's.
  const honorAssigneeTab = !userFilterPayload?.length;
  if (honorAssigneeTab && baseFilters.assigneeType === 'me' && currentUserId) {
    predicates.push({
      attribute_key: 'assignee_id',
      filter_operator: 'equal_to',
      values: [currentUserId],
    });
  } else if (honorAssigneeTab && baseFilters.assigneeType === 'unassigned') {
    predicates.push({
      attribute_key: 'assignee_id',
      filter_operator: 'is_not_present',
      values: [],
    });
  }
  if (baseFilters.labels?.length) {
    predicates.push({
      attribute_key: 'labels',
      filter_operator: 'equal_to',
      values: baseFilters.labels,
    });
  }

  // The API chains predicates by each item's query_operator; the last one must
  // be undefined or the query breaks (mirrors helper/filterQueryGenerator).
  const payload = predicates.map((predicate, index) => ({
    ...predicate,
    query_operator: index === predicates.length - 1 ? undefined : 'and',
  }));

  // The user's advanced filter (or the active folder's query) rides along as a
  // SEPARATE group the backend ANDs with `payload`. It is never merged into
  // `payload`: the backend builds one flat query string per group, so an `or`
  // in the user filter would bind as `A OR (B AND status = 'open')` and the
  // first branch would escape this column.
  const andPayload = userFilterPayload?.length
    ? userFilterPayload.map((condition, index) => ({
        ...condition,
        query_operator:
          index === userFilterPayload.length - 1
            ? undefined
            : condition.query_operator || 'and',
      }))
    : undefined;

  return { queryData: { payload }, andPayload, sortBy: baseFilters.sortBy };
}

// Group a flat list of conversations into buckets keyed by column id.
// Pre-seeds every known column so empty columns still render in order, and
// always exposes a NO_VALUE bucket for conversations missing the field.
export function groupConversations(conversations, groupBy, columns = []) {
  const buckets = { [NO_VALUE_KEY]: [] };
  columns.forEach(column => {
    buckets[column.id] = [];
  });

  (conversations || []).forEach(chat => {
    const key = getGroupValue(chat, groupBy);
    // Route values that don't match a known column (statuses outside the board,
    // or custom values removed from attribute_values) into the no-value column
    // so no conversation silently disappears. Object.hasOwn also avoids
    // matching prototype keys for user-controlled custom-attribute values.
    const target = Object.hasOwn(buckets, key) ? key : NO_VALUE_KEY;
    buckets[target].push(chat);
  });

  return buckets;
}
